import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, FileText, Mail, CheckCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { VialMockupDisplay } from '../components/VialMockup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ProductPage() {
  const { catNo } = useParams();
  const { t, language } = useLanguage();
  const [peptide, setPeptide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPeptide = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API}/peptides/${catNo}?lang=${language}`);
        setPeptide(response.data);
      } catch (err) {
        console.error('Error fetching peptide:', err);
        setError('Peptide not found');
      } finally {
        setLoading(false);
      }
    };
    if (catNo) {
      fetchPeptide();
    }
  }, [catNo, language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20 lg:pt-24">
        <div className="container-custom py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-slate-200 rounded w-32 mb-6" />
            <div className="grid lg:grid-cols-2 gap-8">
              <div className="h-96 bg-slate-200 rounded" />
              <div className="space-y-4">
                <div className="h-6 bg-slate-200 rounded w-1/4" />
                <div className="h-10 bg-slate-200 rounded w-3/4" />
                <div className="h-4 bg-slate-200 rounded w-1/2" />
                <div className="h-32 bg-slate-200 rounded" />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !peptide) {
    return (
      <div className="min-h-screen bg-slate-50 pt-20 lg:pt-24">
        <div className="container-custom py-16 text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-4">Peptide Not Found</h1>
          <p className="text-slate-600 mb-6">The requested peptide could not be found.</p>
          <Link to="/catalog" className="btn-primary inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Catalog
          </Link>
        </div>
      </div>
    );
  }

  // Use translated names when available
  const displayName = peptide.name_translated || peptide.name;
  const displayCategory = peptide.category_translated || peptide.category;

  const specifications = [
    { label: t('productDetail.catalogNumber'), value: peptide.cat_no },
    { label: t('productDetail.concentration'), value: peptide.concentration },
    { label: t('productDetail.category'), value: displayCategory },
    { label: t('products.purity'), value: peptide.purity || '≥99%' },
    ...(peptide.molecular_formula ? [{ label: t('productDetail.molecularFormula'), value: peptide.molecular_formula, mono: true }] : []),
    ...(peptide.molecular_weight ? [{ label: t('productDetail.molecularWeight'), value: peptide.molecular_weight, mono: true }] : []),
    ...(peptide.cas_number ? [{ label: t('productDetail.casNumber'), value: peptide.cas_number, mono: true }] : []),
    { label: t('productDetail.storage'), value: peptide.storage || 'Store at -20°C' },
  ];

  return (
    <div className="min-h-screen bg-slate-50 pt-20 lg:pt-24" data-testid="product-page">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-slate-200">
        <div className="container-custom py-4">
          <Link
            to="/catalog"
            className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="back-to-catalog"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('products.backToCatalog')}
          </Link>
        </div>
      </div>

      <div className="container-custom py-8">
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Left Column - Vial Mockup */}
          <div className="space-y-6">
            {/* Vial Mockup Display - Shows real image if available */}
            <VialMockupDisplay 
              productName={peptide.name} 
              concentration={peptide.concentration} 
              imageUrl={peptide.image_url}
            />

            {/* Research Warning */}
            <div className="flex items-start gap-4 p-4 bg-amber-50 border border-amber-200 rounded-sm">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-amber-900 text-sm">{t('productDetail.researchOnly')}</p>
                <p className="text-xs text-amber-700 mt-1">
                  {language === 'pt' 
                    ? 'Este produto é destinado exclusivamente para pesquisa in-vitro. Não é para consumo humano ou animal.'
                    : 'This product is intended for in-vitro research use only. Not for human or animal consumption.'}
                </p>
              </div>
            </div>
          </div>

          {/* Right Column - Details */}
          <div className="space-y-6">
            {/* Header */}
            <div>
              <p className="text-sm text-slate-500 font-mono mb-2">{peptide.cat_no}</p>
              <h1
                className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2"
                style={{ fontFamily: 'Chivo, sans-serif' }}
                data-testid="product-name"
              >
                {displayName}
              </h1>
              <p className="text-lg text-slate-600">{displayCategory}</p>
            </div>

            {/* Badges */}
            <div className="flex flex-wrap gap-3">
              <span className="badge badge-purity font-mono">{peptide.purity || '≥99%'} Purity</span>
              <span className="badge badge-success">
                <CheckCircle className="w-3 h-3 mr-1" />
                {t('products.inStock')}
              </span>
              <span className="badge badge-warning">
                <AlertTriangle className="w-3 h-3 mr-1" />
                {t('quality.researchOnly')}
              </span>
            </div>

            {/* Tabs */}
            <Tabs defaultValue="description" className="w-full">
              <TabsList className="w-full justify-start bg-slate-100 rounded-sm p-1">
                <TabsTrigger value="description" className="rounded-sm" data-testid="tab-description">
                  {t('productDetail.description')}
                </TabsTrigger>
                <TabsTrigger value="specifications" className="rounded-sm" data-testid="tab-specifications">
                  {t('productDetail.specifications')}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="description" className="mt-6">
                <div className="prose prose-slate max-w-none">
                  <p className="text-slate-600 leading-relaxed">
                    {language === 'pt' ? peptide.description_pt : peptide.description_en}
                  </p>
                  {peptide.sequence && (
                    <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-sm">
                      <p className="text-sm font-semibold text-slate-700 mb-2">{t('productDetail.sequence')}:</p>
                      <p className="font-mono text-xs text-slate-600 break-all">{peptide.sequence}</p>
                    </div>
                  )}
                </div>
              </TabsContent>

              <TabsContent value="specifications" className="mt-6">
                <div className="border border-slate-200 rounded-sm overflow-hidden">
                  <Table>
                    <TableBody>
                      {specifications.map((spec, index) => (
                        <TableRow key={index}>
                          <TableCell className="font-medium text-slate-700 bg-slate-50 w-1/3">
                            {spec.label}
                          </TableCell>
                          <TableCell className={spec.mono ? 'font-mono text-sm' : ''}>
                            {spec.value}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </TabsContent>
            </Tabs>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4 border-t border-slate-200">
              <Link
                to="/coa"
                className="flex-1"
              >
                <Button
                  variant="outline"
                  className="w-full border-slate-200 text-slate-700 hover:bg-slate-50 rounded-sm"
                  data-testid="download-coa-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  {t('productDetail.downloadCoa')}
                </Button>
              </Link>
              <Link
                to="/contact"
                className="flex-1"
              >
                <Button
                  className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm"
                  data-testid="request-quote-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {t('productDetail.requestQuote')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
